// Shared React Query client instance.
// Lives outside main.tsx so non-React code (e.g. the auth store's logout)
// can clear the cache when the user changes — otherwise the next user can be
// served the previous user's cached data (dashboards, lists, ...).
import { QueryClient } from '@tanstack/react-query';

/** True for the browser's raw network failure or a gateway answer during a deploy. */
function isTransientError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const message = error instanceof Error ? error.message : '';
  return /failed to fetch|connexion au serveur interrompue|502|503|504/i.test(message);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      // A real API error (400/403/404) is final; a blip is retried with backoff.
      // resilientFetch already retries a blip 3x; stacking 3 more here turned one
      // restart into 16 requests per query and tripped the per-user rate limit.
      retry: (failureCount, error) => isTransientError(error) && failureCount < 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});
